import { drivelabels_v2 } from '@googleapis/drivelabels';
import { Messages } from '../../../utils/message-constants';
import { DriveRoles } from '../../../utils/roles/client-roles';
import { hasUndeletableLabel } from './label-delete';
import { hasUndisableableLabel } from './label-disable';
import { hasUnenableableLabel } from './label-enable';

type Label = drivelabels_v2.Schema$GoogleAppsDriveLabelsV2Label;

export type LabelToolbarActionKey = 'enable' | 'disable' | 'delete';

export interface LabelToolbarAction {
  key: LabelToolbarActionKey;
  label: string;
  visible: boolean;
  disabled: boolean;
  hint?: string;
}

export interface LabelToolbarContext {
  hasActiveLockByLabelId: Record<string, boolean>;
  canEnable: boolean;
  canDisable: boolean;
  canDelete: boolean;
  /** A delete is running, so Delete waits until it finishes. */
  isDeleting?: boolean;
  hasWriteAccess: boolean | undefined;
}

/**
 * The toolbar's bulk actions for the selected labels. An action is available only when **every**
 * selected label is eligible for it, with the same per-label rules as the row kebab menu:
 *
 * - Disable: every label is published (including with a new draft) and none is locked by a rule.
 * - Enable: every label is disabled. Google documents no lock rule for Enable.
 * - Delete: every label is a draft or disabled.
 *
 * It is not about the labels sharing one lock status: a selection that is entirely locked can't be
 * disabled either. The hint is the first reason that applies: missing role, missing scope, then the
 * status, then (Disable only) a lock. With nothing selected the icons are disabled without a hint.
 */
export function getLabelToolbarActions(
  selectedLabels: Label[],
  context: LabelToolbarContext
): Record<LabelToolbarActionKey, LabelToolbarAction> {
  const {
    hasActiveLockByLabelId,
    canEnable,
    canDisable,
    canDelete,
    isDeleting,
    hasWriteAccess,
  } = context;
  const hasSelection = selectedLabels.length > 0;
  const missingScope = hasWriteAccess === false;

  const build = (
    key: LabelToolbarActionKey,
    label: string,
    allowed: boolean,
    roleName: string,
    statusHint: string | undefined
  ): LabelToolbarAction => {
    const hint = !allowed
      ? Messages.Errors.Permission.Acting(roleName)
      : missingScope
      ? Messages.DriveLabels.NoWriteAccess
      : hasSelection
      ? statusHint
      : undefined;
    return {
      key,
      label,
      visible: true,
      hint,
      disabled: !!hint || hasWriteAccess === undefined || !hasSelection,
    };
  };

  const anyLocked = selectedLabels.some(
    (label) => hasActiveLockByLabelId[label.id ?? '']
  );

  return {
    enable: build(
      'enable',
      'Enable',
      canEnable,
      DriveRoles.UpdateDriveLabelsStatus.name,
      hasUnenableableLabel(selectedLabels)
        ? Messages.DriveLabels.CannotEnableNonDisabled
        : undefined
    ),
    disable: build(
      'disable',
      'Disable',
      canDisable,
      DriveRoles.UpdateDriveLabelsStatus.name,
      hasUndisableableLabel(selectedLabels)
        ? Messages.DriveLabels.CannotDisableUnpublished
        : anyLocked
        ? Messages.DriveLabels.DisableLockedSelectionHint
        : undefined
    ),
    delete: build(
      'delete',
      'Delete',
      canDelete,
      DriveRoles.DeleteDriveLabels.name,
      hasUndeletableLabel(selectedLabels)
        ? Messages.DriveLabels.CannotDeletePublished
        : isDeleting
        ? Messages.DriveLabels.DeleteInProgress
        : undefined
    ),
  };
}
